import React, { useEffect } from 'react';
import { FunctionComponent, useMemo, useState } from 'react';
import { StrictReactNode } from 'record-it-components/types/StrictReactNode';
import { KeyValueStorage } from 'record-it-utils/dataStorage/KeyValueStorage';
import { ApiRequester } from '../classes/ApiRequester/ApiRequester';
import { StorageClearer } from '../classes/StorageClearer/StorageClearer';
import {
  AuthenticationContext,
  AuthenticationContextValue,
  AuthenticationData
} from '../context/AuthenticationContext';

export const AppAuthenticationContextProvider: FunctionComponent<{
  children: StrictReactNode;
}> = React.memo((props) => {
  const { children } = props;
  const [authenticationData, setAuthenticationData] =
    useState<AuthenticationData | null>(null);
  const [initialized, setInitialized] = useState<boolean>(false);

  useEffect(() => {
    void KeyValueStorage.getItem<AuthenticationData>('authenticationData').then(
      (loadedAuthenticationData) => {
        setAuthenticationData(loadedAuthenticationData ?? null);
        setInitialized(true);
      }
    );
  }, []);

  const authenticationContextValue = useMemo<AuthenticationContextValue>(() => {
    ApiRequester.setCsrfToken(authenticationData?.csrfToken ?? null);

    const baseContextValue: Pick<
      AuthenticationContextValue,
      'setAuthenticationData' | 'logout'
    > = {
      setAuthenticationData: (data) => {
        setAuthenticationData(data);
        void KeyValueStorage.setItem('authenticationData', data);
      },
      logout: () => {
        setAuthenticationData(null);
        void StorageClearer.clear();
      }
    };

    if (authenticationData) {
      return {
        ...baseContextValue,
        authenticationData,
        isLoggedIn: true
      };
    }

    return {
      ...baseContextValue,
      authenticationData: null,
      isLoggedIn: false
    };
  }, [authenticationData, setAuthenticationData]);

  return (
    <AuthenticationContext.Provider value={authenticationContextValue}>
      {initialized ? children : undefined}
    </AuthenticationContext.Provider>
  );
});
